import {
  createContext,
  type JSX,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';
import { parseUserMetadata, useAuth } from '../hooks/useAuth';

/**
 * Holds the legacy user id resolved from the Frontegg JWT for the authenticated
 * tree.
 *
 * `null` is the sentinel for "no provider mounted" — e.g. patient routes that
 * must not load `@frontegg/react` (PROD-7582). Consumers treat `null` as
 * "context absent" and fall back to local storage. A mounted provider always
 * yields a string (possibly empty).
 */
const LegacyUserIdContext = createContext<string | null>(null);

/**
 * Returns the legacy user id from context, or `null` when no
 * {@link LegacyUserIdProvider} is mounted above the caller.
 */
export const useLegacyUserIdContext = (): string | null =>
  useContext(LegacyUserIdContext);

type LegacyUserIdProviderProps = {
  children: ReactNode;
};

/**
 * Resolves the legacy user id from the Frontegg JWT `user.metadata` and exposes
 * it through {@link LegacyUserIdContext}.
 *
 * Because it calls Frontegg auth, mount it only in authenticated/provider
 * routes. Patient routes intentionally omit it so the context stays `null` and
 * shared code falls back to storage without pulling in Frontegg (PROD-7582).
 */
export const LegacyUserIdProvider = ({
  children,
}: LegacyUserIdProviderProps): JSX.Element => {
  const { user } = useAuth();

  const legacyUserId = useMemo(() => {
    const { legacyUserId: parsed } = parseUserMetadata(user?.metadata);
    return parsed?.toString() ?? '';
  }, [user?.metadata]);

  return (
    <LegacyUserIdContext.Provider value={legacyUserId}>
      {children}
    </LegacyUserIdContext.Provider>
  );
};
